import React, { useState } from 'react';
import TextArea from 'antd/lib/input/TextArea';
import { Button, Form, Modal, Select } from 'antd';
import { isEmpty } from 'lodash';
import { RejectionReason, UseStateSetter } from 'types';

import { apiPackage } from 'utilities/http/api/leash/apiPackage';
import { LeashContent } from 'leash/utilities/localStorage/leashContentStorage';
import { log } from 'utilities/log/log';
import { ManualActions } from 'leash/pages/details/detailsHeader/ManualActionsMenu';
import { notify } from 'utilities/notifications/notify';
import { removeSequences } from '../sequences/FormSequence.utilities';
import { useDetailsState } from 'leash/pages/details/useDetailsState';

interface Props {
  showModal: boolean;
  setShowModal: UseStateSetter<boolean>;
  rejectedBy: ManualActions;
}

const { Option } = Select;

export function RejectModal(props: Props) {
  const { showModal, setShowModal, rejectedBy } = props;

  const {
    state: { travelPlan, sequences },
    dispatch,
  } = useDetailsState();

  const travelPackage = travelPlan?.travelPackage;

  const [form] = Form.useForm();
  const [isOtherReason, setIsOtherReason] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const onReject = async () => {
    const { reasonId, note } = form.getFieldsValue();
    try {
      setIsRejecting(true);

      const travelSequencesIds =
        sequences?.map((sequence) => sequence.travelSequenceId) || [];

      if (!isEmpty(travelSequencesIds) && travelPlan?.travelPackage.id) {
        await removeSequences(
          travelPlan?.travelPackage.id,
          travelSequencesIds as number[],
        );
        dispatch({ type: 'SET_SEQUENCES', sequences: [] });
      }

      const rejectResponse = await apiPackage.rejectTravelProposal(
        travelPackage?.id as number,
        reasonId,
        rejectedBy,
        note,
      );
      if (rejectResponse.isOk) {
        setShowModal(false);
        notify.success('Travel plan rejected.', 'notify-proposal-rejected');
        dispatch({ type: 'SET_REJECTED_BY', rejectedBy });
      } else {
        notify.error("Couldn't reject the proposal, please try again");
      }
    } catch (e) {
      log.debug('rejecting error', e);
      notify.error("Couldn't reject the proposal, please try again");
    } finally {
      setIsRejecting(false);
    }
  };

  const onClose = () => setShowModal(false);

  const onChangeRejectReason = (e: number) => {
    const otherReasonId = 8;
    setIsOtherReason(e === otherReasonId);
  };

  const [currentNote, setCurrentNote] = useState<string>();
  const onChangeNote = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
    setCurrentNote(e.target.value);
  return (
    <Modal
      title={<strong>Proposal Rejection</strong>}
      visible={showModal}
      closable={true}
      onOk={onReject}
      onCancel={onClose}
      footer={
        <>
          <Button type="primary" ghost onClick={onClose} data-testid="reject-cancel-btn">
            Cancel
          </Button>
          <Button
            data-testid="reject-btn"
            type="primary"
            onClick={onReject}
            loading={isRejecting}
            disabled={isOtherReason && isEmpty(currentNote)}
          >
            OK
          </Button>
        </>
      }
    >
      <Form size="large" layout="vertical" form={form} data-testid="reject-by-form">
        <Form.Item label="Why is the proposal rejected?" name="reasonId">
          <Select
            placeholder="Select a reason why"
            onChange={onChangeRejectReason}
            virtual={false}
            data-testid="reject-reason"
          >
            {LeashContent?.travelRejectionReasons.map((r: RejectionReason) => {
              return (
                <Option value={r.id} key={r.id} data-testid={`reject-id-${r.id}`}>
                  {r.name}
                </Option>
              );
            })}
          </Select>
        </Form.Item>
        {isOtherReason ? (
          <Form.Item label="Add a reason (Required)" name="note">
            <TextArea placeholder="Type here..." onChange={onChangeNote} />
          </Form.Item>
        ) : (
          <Form.Item label="Add a note (Optional)" name="note">
            <TextArea placeholder="Type here..." onChange={onChangeNote} />
          </Form.Item>
        )}
      </Form>
      <span>
        By clicking <strong>OK</strong> you confirm this travel is being rejected and sent
        to the Rejected status.
      </span>
    </Modal>
  );
}
